import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import {
  DeleteNewsletterEmailService,
  GetAllNewsletterEmailsService,
} from "shared/services/newsLetter";
import styles from "./style.module.scss";

interface EmailsData {
  id: number;
  email: string;
  created_at: string;
}

const NewsletterEmails = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    Email: EmailsData | null;
  }>({
    open: false,
    action: null,
    Email: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [Emails, setEmails] = useState<EmailsData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const openConfirmModal = (action: string, Email: EmailsData) => {
    setConfirmModal({ open: true, action, Email });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, Email: null });
  };

  const handleConfirmAction = async () => {
    const id = confirmModal.Email?.id;
    if (!id) return;
    try {
      const response = await DeleteNewsletterEmailService(id);
      if (
        response?.status === 201 ||
        response?.status === 200 ||
        response?.status === 204
      ) {
        toastMessage("success", "Email deleted successfully");
        setEmails((prevEmails) =>
          prevEmails.filter((Email) => Email.id !== id)
        );
        setTotalItems(totalItems - 1);
      } else {
        toastMessage("error", "Failed to delete the Email");
      }
    } catch (error) {
      console.error("Error deleting Email:", error);
      toastMessage("error", "Error: Could not delete Email");
    } finally {
      closeConfirmModal();
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleGetAllEmails(page);
  };

  const EmailsTableHeads = [
    { title: "Email", key: "email" },
    { title: "Date & Time", key: "dateTime" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllEmails = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllNewsletterEmailsService(page);
      const EmailsData = Array.isArray(response.data.data.data)
        ? response.data.data.data
        : [];

      // @ts-ignore
      EmailsData.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA; // Latest emails on top
      });

      setEmails(EmailsData);
      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      console.error("Error fetching Emails:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllEmails();
    // eslint-disable-next-line
  }, []);

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={EmailsTableHeads}
          title="Newsletter Emails List"
          isEmpty={!Emails.length}
          loading={loading}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : Emails.map((items) => {
                return (
                  <tr key={items.id}>
                    <td>{items.email}</td>
                    <td className="text-center">
                      {new Date(items.created_at).toLocaleString()}
                    </td>
                    <td className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="Delete">
                        <button
                          onClick={() => openConfirmModal("Delete", items)}
                        >
                          <Icons.TrashOutline />
                        </button>
                      </CustomToolTip>
                    </td>
                  </tr>
                );
              })}
        </CustomTable>
      </div>

      <div className={classNames(styles.paginationContainer, "mt-3")}>
        <CustomPagination
          onPageChange={handlePageChange}
          totalCount={totalItems}
          currentPage={currentPage}
          pageSize={10}
        />
      </div>

      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          title={`Confirm ${confirmModal.action}`}
          actionButtonText={`Yes, ${confirmModal.action}`}
          subtitle={`Are you sure you want to ${confirmModal.action?.toLowerCase()} this email?`}
          handleSubmit={handleConfirmAction}
          handleClose={closeConfirmModal}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 3 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default NewsletterEmails;
